import React, { useCallback, useEffect, useState } from 'react';
import {
  View,
  Pressable,
  StyleSheet,
  ScrollView,
  Platform,
} from 'react-native';
import { Text, TextInput } from '@/components/common/text';
import DateTimePicker from '@react-native-community/datetimepicker';
import { REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { SVG_COMPONENTS } from '@/assets/admin';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { useMaintenanceForm } from '@/hooks/residents/useMaintenanceForm';
import { useMaintenanceSubmitFlow } from '@/hooks/residents/useMaintenanceSubmitFlow';
import { MaintenanceSubmitStatus } from '@/components/residents/maintenance/shared';
import { useToast } from '@/contexts/admin/ToastContext';
import { useAppSelector } from '@/store';
import { DesktopDropdownSelector, DesktopDropdownOption } from '@/components/common/admin/desktop/DesktopDropdownSelector';
import { MAX_ATTACHMENTS, MAINTENANCE_CONSTANTS } from '@/constants/residents';
import { RadioButton } from '@/components/residents/maintenance/mobile/steps/shared';
import { showConfirm } from '@/utils/common/alert';
import { MediaCarouselUpload } from '@/components/common/residents/MediaCarouselUpload';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useHoverStyle } from '@/hooks/shared/useHoverStyle';
import { useRadius } from '@/hooks/shared/useRadius';
import { useSpacing } from '@/hooks/shared/useSpacing';
import {
  SEVERITY_CHOICES,
  resolveSeverityPriority,
  choiceIdForPriorityName,
} from '@/utils/residents/maintenance/maintenanceSeverity';

const ChevronLeftIcon = SVG_COMPONENTS.CHEVRON_LEFT;

interface DesktopCreateMaintenanceViewProps {
  onClose: () => void;
  onSuccess?: () => void;
}

export const DesktopCreateMaintenanceView: React.FC<DesktopCreateMaintenanceViewProps> = ({
  onClose,
  onSuccess,
}) => {
  const { colors, components, semantic } = useThemeColors();
  const { showError } = useToast();
  const user = useAppSelector((s) => s.auth.user as any);
  const userName = user?.firstName || 'there';

  const {
    description, setDescription,
    location, setLocation,
    category, setCategory,
    subCategory, setSubCategory,
    problem, setProblem,
    severity, setSeverity,
    phoneNumber, setPhoneNumber,
    permissionChoice, setPermissionChoice,
    maintenanceInfo,
    locationOptions,
    categoryOptions,
    subCategoryOptions,
    problemOptions,
    loadInfo,
    isInfoLoading,
    isInfoError,
    infoErrorStatus,
    requestType, setRequestType,
    title, setTitle,
    hasPets, setHasPets,
    specialInstructions, setSpecialInstructions,
    timeSlots, setTimeSlots,
    mediaItems, setMediaItems,
    validate,
  } = useMaintenanceForm();

  const { phase, submit } = useMaintenanceSubmitFlow();

  const priorities = maintenanceInfo?.priorities ?? [];

  const validation = validate();
  const isValid = validation.valid;
  const [submitTooltipVisible, setSubmitTooltipVisible] = useState(false);
  const submitDisabledReason = !isValid ? validation.message : undefined;

  // Web focus highlight: track which field is active so its container can show
  // an accent border (the default browser outline is suppressed via outlineStyle).
  // The focus border sits AFTER the hover border in each style array, so a
  // focused field keeps its accent while the pointer passes over it.
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const focusBorder = (field: string) =>
    focusedField === field ? { borderColor: semantic.interactive.primary } : null;
  const titleHover = useHoverStyle('field');
  const descriptionHover = useHoverStyle('field');
  const instructionsHover = useHoverStyle('field');
  // The radio rows are bare (radio + label): a padded box pulled back by the
  // same amounts gives the row tint a shape without moving anything.
  const radius = useRadius();
  const space = useSpacing();
  const radioBox = {
    paddingHorizontal: space.sm,
    marginHorizontal: -space.sm,
    paddingVertical: space.xs,
    marginVertical: -space.xs,
    borderRadius: radius.sm,
  };

  // Time-slot picker bounds — mirror the "next 10 days" hint and the mobile flow.
  const slotMinDate = new Date();
  const slotMaxDate = new Date(Date.now() + 10 * 24 * 60 * 60 * 1000);

  const isDirty =
    title.trim() !== '' ||
    description.trim() !== '' ||
    mediaItems.length > 0 ||
    category !== '' ||
    severity !== '';

  const handleClose = useCallback(async () => {
    if (!isDirty) {
      onClose();
      return;
    }
    const confirmed = await showConfirm(
      'Discard Request?',
      'Are you sure you want to discard your work request?',
      'Discard',
      'Cancel',
    );
    if (confirmed) onClose();
  }, [isDirty, onClose]);

  useEffect(() => {
    loadInfo();
  }, [loadInfo]);

  useEffect(() => {
    if (isInfoError && (infoErrorStatus === 500 || infoErrorStatus == null)) {
      console.error('Unable to load maintenance options');
    }
  }, [isInfoError, infoErrorStatus]);

  const handleSubmit = useCallback(async () => {
    if (!isValid) return;
    await submit({
      requestType,
      category,
      subCategory,
      maintenanceInfo,
      location,
      problem,
      severity,
      title,
      description,
      phoneNumber,
      permissionChoice,
      attachments: mediaItems as any,
      hasPets: hasPets ?? undefined,
      specialInstructions,
    });
  }, [
    requestType, subCategory, severity, title, permissionChoice,
    category, maintenanceInfo, location, problem, description,
    phoneNumber, mediaItems, hasPets, specialInstructions,
    isValid, submit,
  ]);

  const handleDone = useCallback(() => {
    onSuccess?.();
    onClose();
  }, [onSuccess, onClose]);

  const selectedChoiceId = choiceIdForPriorityName(severity, priorities);

  if (phase !== 'form') {
    return (
      <View style={styles.statusContainer}>
        <View style={styles.statusCard}>
          <MaintenanceSubmitStatus
            phase={phase}
            hasAttachments={mediaItems.length > 0}
            onDone={handleDone}
          />
        </View>
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <View style={[styles.header, { borderBottomColor: colors.card.border }]}>
        <Hoverable
          hover="glass"
          style={[styles.backButton, { backgroundColor: colors.glass.buttonBg, borderColor: colors.glass.buttonBorder }]}
          onPress={handleClose}
        >
          <ChevronLeftIcon width={24} height={24} color={colors.text.muted} />
        </Hoverable>
        <Text style={[styles.headerTitle, { color: colors.text.primary }]}>
          Hi {userName}, what do you need help with?
        </Text>
      </View>

      <View style={styles.formContainer}>
        {/* Section 1: Issue */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>Issue</Text>
          <View style={styles.categoryRow}>
            <Hoverable
              hover={requestType === 'maintenance' ? 'none' : 'field'}
              style={[
                styles.categoryCard,
                requestType === 'maintenance'
                  ? { backgroundColor: colors.accent.blueBg, borderColor: semantic.interactive.primary }
                  : { backgroundColor: colors.card.bg, borderColor: colors.card.border },
              ]}
              onPress={() => setRequestType('maintenance')}
            >
              <View style={[styles.categoryIconContainer, { backgroundColor: colors.glass.buttonBg }]}>
                <RESIDENTS_SVG_COMPONENTS.HAND_WRENCH width={20} height={20} />
              </View>
              <View style={styles.categoryContent}>
                <View style={styles.categoryHeader}>
                  <Text style={[styles.categoryName, { color: colors.text.primary }]}>Maintenance</Text>
                  <RadioButton selected={requestType === 'maintenance'} />
                </View>
                <Text style={[styles.categoryDescription, { color: colors.text.dim }]}>
                  Report issues related to repairs or upkeep
                </Text>
              </View>
            </Hoverable>

            <Hoverable
              hover={requestType === 'it' ? 'none' : 'field'}
              style={[
                styles.categoryCard,
                requestType === 'it'
                  ? { backgroundColor: colors.accent.blueBg, borderColor: semantic.interactive.primary }
                  : { backgroundColor: colors.card.bg, borderColor: colors.card.border },
              ]}
              onPress={() => setRequestType('it')}
            >
              <View style={[styles.categoryIconContainer, { backgroundColor: colors.glass.buttonBg }]}>
                <RESIDENTS_SVG_COMPONENTS.WIFI width={20} height={20} color={colors.text.primary} />
              </View>
              <View style={styles.categoryContent}>
                <View style={styles.categoryHeader}>
                  <Text style={[styles.categoryName, { color: colors.text.primary }]}>IT</Text>
                  <RadioButton selected={requestType === 'it'} />
                </View>
                <Text style={[styles.categoryDescription, { color: colors.text.dim }]}>
                  Report issues with the app or system
                </Text>
              </View>
            </Hoverable>
          </View>
          {requestType === 'maintenance' && (isInfoLoading || subCategoryOptions.length > 0) && (
            // Reserve the field's space as soon as Maintenance is selected so the
            // async subcategory options fill it IN PLACE, instead of popping in a
            // second later and shoving the rest of the form down. Dimmed and
            // non-interactive until the options finish loading.
            <View
              style={[styles.subcategoryField, isInfoLoading && styles.subcategoryLoading]}
              pointerEvents={isInfoLoading ? 'none' : 'auto'}
            >
              <DesktopDropdownSelector
                label="Subcategory"
                placeholder={isInfoLoading ? 'Loading…' : 'Subcategory'}
                selectedValue={subCategory}
                options={subCategoryOptions.map<DesktopDropdownOption>((s) => ({
                  label: s,
                  value: s
                }))}
                onChange={setSubCategory}
                selectorStyle={{ backgroundColor: colors.card.bg, borderColor: colors.card.border }}
              />
            </View>
          )}
        </View>

        {/* Section 2: Details */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>Details</Text>
          <View style={styles.detailsRow}>
            <View style={styles.detailsLeft}>
              <View
                style={[styles.inputContainer, { backgroundColor: colors.glass.buttonBg, borderColor: colors.card.border }, titleHover.viewStyle, focusBorder('title')]}
                {...(titleHover.mouseProps as any)}
              >
                <Text style={[styles.inputLabel, { color: colors.text.muted }]}>Brief title</Text>
                <TextInput
                  style={[styles.titleInput, { color: colors.text.primary }]}
                  value={title}
                  onChangeText={setTitle}
                  onFocus={() => setFocusedField('title')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Enter a brief title"
                  placeholderTextColor={colors.text.dimmer}
                />
              </View>
              <View
                style={[styles.inputContainer, styles.descriptionContainer, { backgroundColor: colors.glass.buttonBg, borderColor: colors.card.border }, descriptionHover.viewStyle, focusBorder('description')]}
                {...(descriptionHover.mouseProps as any)}
              >
                <TextInput
                  style={[styles.descriptionInput, { color: colors.text.primary }]}
                  value={description}
                  onChangeText={setDescription}
                  onFocus={() => setFocusedField('description')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="Description"
                  placeholderTextColor={colors.text.dimmer}
                  multiline
                  numberOfLines={10}
                  textAlignVertical="top"
                />
              </View>
            </View>

            <View style={styles.detailsRight}>
              <MediaCarouselUpload
                files={mediaItems}
                onFilesChange={setMediaItems}
                config={{
                  maxFiles: MAX_ATTACHMENTS,
                  maxFileSizeMB: 2,
                  uploadAreaHeight: 384,
                }}
                onError={showError}
                variant="dark"
              />
            </View>
          </View>
        </View>

        {/* Section 3: Urgency */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>Urgency</Text>
          <View style={styles.severityGrid}>
            {SEVERITY_CHOICES.map((choice) => {
              const selected = selectedChoiceId === choice.id;
              return (
                <Hoverable
                  key={choice.id}
                  hover={selected ? 'none' : 'field'}
                  style={[
                    styles.severityItem,
                    selected
                      ? { backgroundColor: colors.accent.blueBg, borderColor: semantic.interactive.primary }
                      : { backgroundColor: colors.card.bg, borderColor: colors.card.border },
                  ]}
                  onPress={() => {
                    const p = resolveSeverityPriority(choice.id, priorities);
                    if (p) setSeverity(p.name);
                  }}
                >
                  <Text style={[styles.severityText, { color: colors.text.primary }]}>{choice.label}</Text>
                  <RadioButton selected={selected} />
                </Hoverable>
              );
            })}
          </View>
        </View>

        {/* Section 4: Access */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text.primary }]}>Access</Text>
          <View style={styles.accessRow}>
          <View style={[styles.accessGroup, styles.accessColumn]}>
          <Text style={[styles.accessSubtitle, { color: colors.text.muted }]}>Can our technician enter your residence?</Text>
          <View style={styles.radioGroup}>
            <Hoverable
              hover="row"
              style={[styles.radioRow, radioBox]}
              onPress={() => setPermissionChoice('yes')}
            >
              <RadioButton selected={permissionChoice === 'yes'} />
              <Text style={[styles.radioLabel, { color: colors.text.primary }]}>Yes</Text>
            </Hoverable>
            <View style={styles.radioColumn}>
              <Hoverable
                hover="row"
                style={[styles.radioRow, radioBox]}
                onPress={() => setPermissionChoice('no')}
              >
                <RadioButton selected={permissionChoice === 'no'} />
                <Text style={[styles.radioLabel, { color: colors.text.primary }]}>No</Text>
              </Hoverable>

              {permissionChoice === 'no' && (
                <View style={styles.timeSlotsContainer}>
                  <View style={[styles.alertBox, { backgroundColor: colors.accent.orangeLight }]}>
                    <Text style={[styles.alertIcon, { color: semantic.status.warning }]}>⚠</Text>
                    <Text style={[styles.alertText, { color: semantic.status.warning }]}>
                      Please select {MAINTENANCE_CONSTANTS.MINIMUM_TIME_SLOTS} time slots in the next 10 days
                    </Text>
                  </View>
                  {[0, 1, 2].map((index) => (
                    <View key={index} style={[styles.timeSlotInput, { backgroundColor: colors.card.bg, borderBottomColor: colors.text.primary }]}>
                      <Text style={[styles.timeSlotLabel, { color: colors.text.muted }]}>Date and time</Text>
                      <DateTimePicker
                        mode="datetime"
                        // Empty slots render an empty picker (undefined value); stored
                        // values round-trip as ISO strings, matching the mobile flow.
                        value={timeSlots[index] ? new Date(timeSlots[index]) : (undefined as unknown as Date)}
                        minimumDate={slotMinDate}
                        maximumDate={slotMaxDate}
                        onChange={(_event, date) => {
                          if (!date) return;
                          const newSlots = [...timeSlots];
                          newSlots[index] = date.toISOString();
                          setTimeSlots(newSlots);
                        }}
                        // Cast: web mock consumes these as DOM CSS (color/fontSize valid
                        // there) so the picker blends into the bordered container.
                        style={{ width: '100%', borderWidth: 0, backgroundColor: 'transparent', color: colors.text.primary, fontSize: 16, padding: 0 } as any}
                      />
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
          </View>

          <View style={[styles.accessGroup, styles.accessColumn]}>
          <Text style={[styles.accessSubtitle, { color: colors.text.muted }]}>Do you have pets?</Text>
          <View style={styles.radioGroup}>
            <Hoverable
              hover="row"
              style={[styles.radioRow, radioBox]}
              onPress={() => setHasPets(true)}
            >
              <RadioButton selected={hasPets === true} />
              <Text style={[styles.radioLabel, { color: colors.text.primary }]}>Yes</Text>
            </Hoverable>
            <Hoverable
              hover="row"
              style={[styles.radioRow, radioBox]}
              onPress={() => setHasPets(false)}
            >
              <RadioButton selected={hasPets === false} />
              <Text style={[styles.radioLabel, { color: colors.text.primary }]}>No</Text>
            </Hoverable>
          </View>
          </View>
          </View>

          <View style={styles.accessGroup}>
          <Text style={[styles.accessSubtitle, { color: colors.text.muted }]}>Special instructions</Text>
          <View
            style={[styles.additionalInput, { backgroundColor: colors.glass.buttonBg, borderColor: colors.card.border }, instructionsHover.viewStyle, focusBorder('instructions')]}
            {...(instructionsHover.mouseProps as any)}
          >
            <TextInput
              style={[styles.additionalTextInput, { color: colors.text.primary }]}
              value={specialInstructions}
              onChangeText={setSpecialInstructions}
              onFocus={() => setFocusedField('instructions')}
              onBlur={() => setFocusedField(null)}
              placeholder="Special entry instructions"
              placeholderTextColor={colors.text.dimmer}
            />
          </View>
          </View>
        </View>

        <Pressable
          style={styles.submitWrapper}
          onHoverIn={() => setSubmitTooltipVisible(true)}
          onHoverOut={() => setSubmitTooltipVisible(false)}
        >
          {submitDisabledReason && submitTooltipVisible ? (
            <View
              style={[
                styles.submitTooltip,
                { backgroundColor: colors.card.bg, borderColor: colors.card.border },
              ]}
              pointerEvents="none"
            >
              <Text style={[styles.submitTooltipText, { color: colors.text.primary }]}>
                {submitDisabledReason}
              </Text>
            </View>
          ) : null}
          <Hoverable
            hover="button"
            style={[
              styles.submitButton,
              { backgroundColor: semantic.interactive.primary },
              !isValid && styles.submitButtonDisabled,
            ]}
            onPress={handleSubmit}
            disabled={!isValid}
          >
            <Text style={[styles.submitButtonText, { color: components.button.primaryText }]}>
              Submit work request
            </Text>
          </Hoverable>
        </Pressable>
      </View>
    </ScrollView>
  );
};

// Shared half-width for standalone fields so they align to one grid and shrink
// gracefully on narrow windows instead of clipping at a hard pixel width.
const FORM_FIELD_HALF = 389;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  contentContainer: {
    flexGrow: 1,
  },
  header: {
    height: 93,
    borderBottomWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: 24,
    width: 40,
    height: 40,
    borderWidth: 1,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 30,
    fontWeight: '600',
    lineHeight: 45,
    textAlign: 'center',
  },
  formContainer: {
    width: '100%',
    maxWidth: 866,
    alignSelf: 'center',
    paddingHorizontal: 32,
    paddingVertical: 40,
    gap: 28,
  },
  section: {
    gap: 16,
  },
  sectionTitle: {
    ...REDESIGN_TYPOGRAPHY.cardTitleLarge,
    fontWeight: '500',
    lineHeight: 24,
  },
  categoryRow: {
    flexDirection: 'row',
    gap: 24,
  },
  categoryCard: {
    flex: 1,
    borderWidth: 1,
    borderRadius: 8,
    padding: 16,
    paddingBottom: 20,
    flexDirection: 'row',
    gap: 16,
  },
  categoryIconContainer: {
    width: 46,
    height: 46,
    borderRadius: 23,
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoryContent: {
    flex: 1,
    gap: 6,
  },
  categoryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  categoryName: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
  },
  categoryDescription: {
    ...REDESIGN_TYPOGRAPHY.caption,
    fontWeight: '300',
    lineHeight: 18,
  },
  subcategoryField: {
    width: '100%',
    maxWidth: FORM_FIELD_HALF,
  },
  subcategoryLoading: {
    opacity: 0.55,
  },
  severityGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 24,
  },
  severityItem: {
    flexGrow: 1,
    flexShrink: 0,
    flexBasis: '47%',
    borderWidth: 1,
    borderRadius: 16,
    paddingHorizontal: 20,
    paddingVertical: 16,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  severityText: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
    flex: 1,
  },
  detailsRow: {
    flexDirection: 'row',
    gap: 24,
  },
  detailsLeft: {
    flex: 1,
    gap: 24,
  },
  detailsRight: {
    flex: 1,
  },
  inputContainer: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingTop: 4,
    paddingBottom: 8,
  },
  inputLabel: {
    fontSize: 10,
    fontWeight: '600',
    lineHeight: 12.5,
  },
  titleInput: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 17.5,
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
    } as any)),
  },
  descriptionContainer: {
    flex: 1,
    minHeight: 280,
    paddingTop: 11,
  },
  descriptionInput: {
    flex: 1,
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
    } as any)),
  },
  accessRow: {
    flexDirection: 'row',
    gap: 24,
    alignItems: 'flex-start',
  },
  accessColumn: {
    flex: 1,
  },
  accessGroup: {
    gap: 12,
  },
  accessSubtitle: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontWeight: '500',
    lineHeight: 21,
  },
  radioGroup: {
    gap: 12,
  },
  radioColumn: {
    gap: 12,
  },
  radioRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  radioLabel: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  timeSlotsContainer: {
    marginLeft: 26,
    gap: 16,
    width: '100%',
    maxWidth: FORM_FIELD_HALF,
  },
  alertBox: {
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  alertIcon: {
    fontSize: 16,
  },
  alertText: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 21,
    flex: 1,
  },
  timeSlotInput: {
    borderBottomWidth: 1,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    paddingHorizontal: 12,
    paddingTop: 6,
    paddingBottom: 4,
  },
  timeSlotLabel: {
    ...REDESIGN_TYPOGRAPHY.caption,
    lineHeight: 12,
  },
  additionalInput: {
    width: '100%',
    maxWidth: FORM_FIELD_HALF,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  additionalTextInput: {
    ...REDESIGN_TYPOGRAPHY.body,
    lineHeight: 17.5,
    ...(Platform.OS === 'web' && ({
      outlineStyle: 'none',
    } as any)),
  },
  submitWrapper: {
    alignSelf: 'flex-start',
    position: 'relative',
  },
  submitTooltip: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    marginBottom: 8,
    maxWidth: 280,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    zIndex: 10,
  },
  submitTooltipText: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontSize: 12,
    lineHeight: 16,
  },
  submitButton: {
    borderRadius: 8,
    height: 40,
    paddingHorizontal: 16,
    paddingVertical: 8,
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'flex-start',
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    ...REDESIGN_TYPOGRAPHY.body,
    fontWeight: '500',
    lineHeight: 21,
  },
  statusContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  statusCard: {
    width: '100%',
    maxWidth: 480,
    minHeight: 360,
  },
});

export default DesktopCreateMaintenanceView;
